import cuentasEmpresaService from '@/services/cuentasEmpresaService';
import { ESTADO_CUENTA } from '@/utils/estadoCuenta';
import { descripcionCuenta } from '@/utils/cuentaEmpresa';

/**
 * Cuentas de empresa + vinculo N:N con medios de pago (S4b).
 *
 * `cuentas` and `medios` keep separate loading/error state on purpose: opening the link
 * editor for one account must not blank out the account list rendered behind it.
 *
 * REQ-CTA-004 — `activo` is a TINYINT with THREE states: 1 activa, 2 inactiva, 0 eliminada.
 * Never test it for truthiness: 2 is truthy and the account is NOT active.
 */

const state = () => ({
  cuentas: [],
  cuentasLoading: false,
  cuentasError: null,
  medios: [],
  mediosLoading: false,
  mediosError: null,
  catalogoMedios: [],
  // S6 (T6.9) — diagnostico + confirmacion de corte historico. Estado propio: cargar el
  // diagnostico o confirmar el corte no debe pisar el loading/error de cuentas/medios.
  diagnostico: null,
  diagnosticoLoading: false,
  diagnosticoError: null,
  confirmarCorteLoading: false,
  confirmarCorteError: null,
  // S7 (T7.11) — estado de cuenta. Estado propio: consultar/descargar el estado de cuenta no
  // debe pisar el loading/error de cuentas/medios/corte.
  estadoCuenta: null,
  estadoCuentaLoading: false,
  estadoCuentaError: null,
  estadoCuentaDescargando: false,
  // S8 (T8.11) — reimputación. Listado de cuentas activas candidatas a destino, compuesto
  // del lado del cliente (ver fetchCuentasParaMedioPago). Estado propio: no pisa
  // cuentas/cuentasLoading, que alimenta el ABM.
  cuentasDestinoReimputacion: [],
  cuentasDestinoReimputacionLoading: false,
  cuentasDestinoReimputacionError: null,
  // Trello #350 — destinos (cuenta Y medio) para reimputar, agrupados por medio. Reemplaza a
  // cuentasDestinoReimputacion en DialogReimputarPago.vue: ese listado sólo ofrecía cuentas del
  // MISMO medio del cobro, dejando sin destino a un cobro cargado con el medio equivocado. Se
  // mantiene el estado viejo (no se borra `fetchCuentasParaMedioPago`) porque no rompe nada
  // dejarlo — es la conducta anterior, aún referenciada por sus propios tests de store.
  destinosReimputacion: [],
  destinosReimputacionLoading: false,
  destinosReimputacionError: null,
  // WU6c (`conciliacion-externa`, CFG-001) — parámetros de acreditación por par cuenta<->medio,
  // flatten de TODAS las cuentas activas x sus medios vinculados (no de una sola cuenta
  // seleccionada, a diferencia de `medios`). Estado propio: no pisa cuentas/medios/etc.
  parametrosAcreditacion: [],
  parametrosAcreditacionLoading: false,
  parametrosAcreditacionError: null,
  actualizandoParametro: false,
  actualizarParametroError: null,
});

const getters = {
  cuentasActivas: (state) =>
    state.cuentas.filter((c) => Number(c.activo) === ESTADO_CUENTA.ACTIVA),

  cuentasNoEliminadas: (state) =>
    state.cuentas.filter((c) => Number(c.activo) !== ESTADO_CUENTA.ELIMINADA),

  getCuentaById: (state) => (id) =>
    state.cuentas.find((c) => c.id_cuenta === id),

  // Medios del catalogo que la cuenta abierta todavia no tiene vinculados.
  mediosDisponibles: (state) => {
    const vinculados = new Set(state.medios.map((m) => m.id_medio_pago));
    return state.catalogoMedios.filter((m) => !vinculados.has(m.id_medio_pago));
  },
};

const desempaquetar = (respuesta) => respuesta?.data?.data ?? [];

/**
 * El backend trae el vinculo con `include: [{ model: MedioPago, as: 'medio_pago' }]`, asi que el
 * nombre y el `mueve_dinero` llegan ANIDADOS bajo `medio_pago` y NO en la raiz de la fila.
 * Leerlos de la raiz da un objeto (que la vista renderiza como JSON) y un `undefined` (que deja
 * el switch siempre apagado, en silencio). Se aplanan aca para que la vista no sepa de la forma
 * de la asociacion.
 */
const aplanarVinculo = (vinculo) => ({
  ...vinculo,
  nombre_medio: vinculo?.medio_pago?.medio_pago || `Medio ${vinculo?.id_medio_pago}`,
  mueve_dinero: vinculo?.medio_pago?.mueve_dinero ?? null,
});

const actions = {
  async fetchCuentas({ commit }, filtros = {}) {
    try {
      commit('SET_CUENTAS_LOADING', true);
      const respuesta = await cuentasEmpresaService.obtenerCuentas(filtros);
      commit('SET_CUENTAS', desempaquetar(respuesta));
      commit('SET_CUENTAS_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_CUENTAS_ERROR', error.message || 'Error al cargar las cuentas');
      throw error;
    } finally {
      commit('SET_CUENTAS_LOADING', false);
    }
  },

  async crearCuenta({ commit, dispatch }, datos) {
    try {
      commit('SET_CUENTAS_LOADING', true);
      const respuesta = await cuentasEmpresaService.crearCuenta(datos);
      await dispatch('fetchCuentas');
      return respuesta;
    } catch (error) {
      commit('SET_CUENTAS_ERROR', error.message || 'Error al crear la cuenta');
      throw error;
    } finally {
      commit('SET_CUENTAS_LOADING', false);
    }
  },

  async actualizarCuenta({ commit, dispatch }, { id, datos }) {
    try {
      commit('SET_CUENTAS_LOADING', true);
      const respuesta = await cuentasEmpresaService.actualizarCuenta(id, datos);
      await dispatch('fetchCuentas');
      return respuesta;
    } catch (error) {
      commit('SET_CUENTAS_ERROR', error.message || 'Error al actualizar la cuenta');
      throw error;
    } finally {
      commit('SET_CUENTAS_LOADING', false);
    }
  },

  async eliminarCuenta({ commit, dispatch }, id) {
    try {
      commit('SET_CUENTAS_LOADING', true);
      const respuesta = await cuentasEmpresaService.eliminarCuenta(id);
      await dispatch('fetchCuentas');
      return respuesta;
    } catch (error) {
      commit('SET_CUENTAS_ERROR', error.message || 'Error al eliminar la cuenta');
      throw error;
    } finally {
      commit('SET_CUENTAS_LOADING', false);
    }
  },

  /**
   * `cobros/tipos-pago` devuelve un ARRAY PELADO, no el envelope { data: { data } } de los
   * endpoints de `pagos`. Desempaquetarlo como a los otros daria [] en silencio.
   */
  async fetchCatalogoMedios({ commit }) {
    try {
      const respuesta = await cuentasEmpresaService.obtenerCatalogoMedios();
      const catalogo = Array.isArray(respuesta?.data) ? respuesta.data : [];
      commit('SET_CATALOGO_MEDIOS', catalogo);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al cargar los medios de pago');
      throw error;
    }
  },

  /**
   * Cuentas activas de un medio, para el selector de la pantalla de cobro (S4c).
   *
   * DEVUELVE la lista en vez de commitearla a proposito: el selector puede estar montado en
   * varias pantallas de cobro a la vez y un estado compartido las haria pisarse. La llamada a
   * la API sigue centralizada aca, que es lo que pide la convencion del repo.
   */
  async fetchCuentasDeMedio(_ctx, id_medio_pago) {
    const respuesta = await cuentasEmpresaService.obtenerCuentasDeMedio(id_medio_pago);
    return desempaquetar(respuesta).map((v) => ({
      id_cuenta: v.id_cuenta,
      es_default: v.es_default,
      nombre: v?.cuenta?.cuenta || `Cuenta ${v.id_cuenta}`,
      descripcion: descripcionCuenta(v?.cuenta),
    }));
  },

  // ─── Vinculos ─────────────────────────────────────────────────────────────

  async fetchMediosDeCuenta({ commit }, id) {
    try {
      commit('SET_MEDIOS_LOADING', true);
      const respuesta = await cuentasEmpresaService.listarMediosDeCuenta(id);
      commit('SET_MEDIOS', desempaquetar(respuesta).map(aplanarVinculo));
      commit('SET_MEDIOS_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al cargar los medios vinculados');
      throw error;
    } finally {
      commit('SET_MEDIOS_LOADING', false);
    }
  },

  async vincularMedio({ commit, dispatch }, { id, id_medio_pago, es_default = 0 }) {
    try {
      commit('SET_MEDIOS_LOADING', true);
      const respuesta = await cuentasEmpresaService.vincularMedio(id, { id_medio_pago, es_default });
      await dispatch('fetchMediosDeCuenta', id);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al vincular el medio de pago');
      throw error;
    } finally {
      commit('SET_MEDIOS_LOADING', false);
    }
  },

  async desvincularMedio({ commit, dispatch }, { id, id_medio_pago }) {
    try {
      commit('SET_MEDIOS_LOADING', true);
      const respuesta = await cuentasEmpresaService.desvincularMedio(id, id_medio_pago);
      await dispatch('fetchMediosDeCuenta', id);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al desvincular el medio de pago');
      throw error;
    } finally {
      commit('SET_MEDIOS_LOADING', false);
    }
  },

  /**
   * Moving the default is a per-medio operation, but the editor is rendered per-cuenta,
   * so the caller passes `id_cuenta_actual` to say which list to refresh afterwards.
   */
  async marcarCuentaDefault({ commit, dispatch }, { id_medio_pago, id_cuenta, id_cuenta_actual }) {
    try {
      commit('SET_MEDIOS_LOADING', true);
      const respuesta = await cuentasEmpresaService.marcarCuentaDefault(id_medio_pago, id_cuenta);
      if (id_cuenta_actual != null) await dispatch('fetchMediosDeCuenta', id_cuenta_actual);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al marcar la cuenta predeterminada');
      throw error;
    } finally {
      commit('SET_MEDIOS_LOADING', false);
    }
  },

  async actualizarMueveDinero({ commit, dispatch }, { id_medio_pago, mueve_dinero, id_cuenta_actual }) {
    try {
      commit('SET_MEDIOS_LOADING', true);
      const respuesta = await cuentasEmpresaService.actualizarMueveDinero(id_medio_pago, mueve_dinero);
      if (id_cuenta_actual != null) await dispatch('fetchMediosDeCuenta', id_cuenta_actual);
      return respuesta;
    } catch (error) {
      commit('SET_MEDIOS_ERROR', error.message || 'Error al actualizar el medio de pago');
      throw error;
    } finally {
      commit('SET_MEDIOS_LOADING', false);
    }
  },

  // ─── Corte historico + apertura (S6, T6.9) ─────────────────────────────────

  async fetchDiagnosticoCorte({ commit }, umbral) {
    try {
      commit('SET_DIAGNOSTICO_LOADING', true);
      const respuesta = await cuentasEmpresaService.diagnosticoCorte(umbral);
      commit('SET_DIAGNOSTICO', respuesta?.data?.data ?? null);
      commit('SET_DIAGNOSTICO_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_DIAGNOSTICO_ERROR', error.message || 'Error al calcular el diagnostico de corte');
      throw error;
    } finally {
      commit('SET_DIAGNOSTICO_LOADING', false);
    }
  },

  /**
   * No refetches nada al terminar: el corte es un evento unico (idempotente en el backend) y
   * la vista decide que hacer con la respuesta (mostrar el resumen de apertura por cuenta).
   * Rethrows el error ORIGINAL (no uno nuevo) para que la vista pueda leer
   * error.response.data.message y distinguir el caso idempotente ("ya fue confirmado") de un
   * error de negocio genuino, en vez de mostrar siempre el mismo toast crudo.
   */
  async confirmarCorte({ commit }, { fecha_corte, saldos_declarados } = {}) {
    try {
      commit('SET_CONFIRMAR_CORTE_LOADING', true);
      const respuesta = await cuentasEmpresaService.confirmarCorte({ fecha_corte, saldos_declarados });
      commit('SET_CONFIRMAR_CORTE_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_CONFIRMAR_CORTE_ERROR', error.message || 'Error al confirmar el corte');
      throw error;
    } finally {
      commit('SET_CONFIRMAR_CORTE_LOADING', false);
    }
  },

  // ─── Estado de cuenta (S7, T7.11) ──────────────────────────────────────────

  /**
   * `error.response.data.msg` (no `.message` — distinto del resto del módulo, ver
   * `getEstadoCuentaController`) trae el texto de negocio; `type` distingue el gate NFR-003
   * ('warning', la migración DECIMAL todavía no corrió en el tenant) de un error real
   * ('error'). Se persiste el par {mensaje, tipo} completo para que la vista pueda pintar la
   * alerta con el color correcto en vez de mostrar siempre rojo.
   */
  async fetchEstadoCuenta({ commit }, { id_cuenta, fecha_desde, fecha_hasta, page, limit } = {}) {
    try {
      commit('SET_ESTADO_CUENTA_LOADING', true);
      const respuesta = await cuentasEmpresaService.obtenerEstadoCuenta({
        id_cuenta,
        fecha_desde,
        fecha_hasta,
        page,
        limit,
      });
      commit('SET_ESTADO_CUENTA', respuesta?.data ?? null);
      commit('SET_ESTADO_CUENTA_ERROR', null);
      return respuesta;
    } catch (error) {
      commit('SET_ESTADO_CUENTA_ERROR', {
        mensaje: error.response?.data?.msg || error.message || 'Error al calcular el estado de cuenta',
        tipo: error.response?.data?.type || 'error',
      });
      throw error;
    } finally {
      commit('SET_ESTADO_CUENTA_LOADING', false);
    }
  },

  async descargarEstadoCuenta({ commit }, { id_cuenta, fecha_desde, fecha_hasta } = {}) {
    try {
      commit('SET_ESTADO_CUENTA_DESCARGANDO', true);
      return await cuentasEmpresaService.descargarEstadoCuenta({ id_cuenta, fecha_desde, fecha_hasta });
    } finally {
      commit('SET_ESTADO_CUENTA_DESCARGANDO', false);
    }
  },

  // ─── Reimputación — cuentas destino candidatas (S8, T8.11) ────────────────

  /**
   * Mismo endpoint que usa SelectorCuentaDestino.vue en la pantalla de cobro
   * (GET pagos/medios-pago/:id/cuentas, S4c) — el backend ya resuelve el N:N
   * cuenta<->medio de pago en una sola consulta.
   */
  async fetchCuentasParaMedioPago({ commit }, { id_medio_pago, id_cuenta_excluir } = {}) {
    try {
      commit('SET_CUENTAS_DESTINO_REIMPUTACION_LOADING', true);
      commit('SET_CUENTAS_DESTINO_REIMPUTACION_ERROR', null);
      const respuesta = await cuentasEmpresaService.obtenerCuentasDeMedio(id_medio_pago);
      const destino = desempaquetar(respuesta).filter((c) => c.id_cuenta !== id_cuenta_excluir);
      commit('SET_CUENTAS_DESTINO_REIMPUTACION', destino);
      return destino;
    } catch (error) {
      commit(
        'SET_CUENTAS_DESTINO_REIMPUTACION_ERROR',
        error.message || 'Error al cargar las cuentas destino'
      );
      throw error;
    } finally {
      commit('SET_CUENTAS_DESTINO_REIMPUTACION_LOADING', false);
    }
  },

  /**
   * Destinos posibles (cuenta Y medio) para reimputar un cobro (Trello #350). A diferencia de
   * `fetchCuentasParaMedioPago`, no recibe un `id_medio_pago` — el backend ya resuelve, a partir
   * del `id_pago`, todas las combinaciones activas cuenta<->medio agrupadas por medio.
   */
  async fetchDestinosReimputacion({ commit }, id_pago) {
    try {
      commit('SET_DESTINOS_REIMPUTACION_LOADING', true);
      commit('SET_DESTINOS_REIMPUTACION_ERROR', null);
      // JD ronda 1 #350 (WARNING 5): se limpia ANTES de pedir los nuevos, no recién cuando la
      // respuesta llega — reabrir el diálogo para OTRO cobro mientras este fetch está en vuelo no
      // debe dejar visible (ni confirmable, ver `puedeConfirmar` de DialogReimputarPago.vue) la
      // lista de destinos del cobro anterior.
      commit('SET_DESTINOS_REIMPUTACION', []);
      const respuesta = await cuentasEmpresaService.obtenerDestinosReimputacion(id_pago);
      const destinos = desempaquetar(respuesta);
      commit('SET_DESTINOS_REIMPUTACION', destinos);
      return destinos;
    } catch (error) {
      commit(
        'SET_DESTINOS_REIMPUTACION_ERROR',
        error.message || 'Error al cargar los destinos de reimputación'
      );
      throw error;
    } finally {
      commit('SET_DESTINOS_REIMPUTACION_LOADING', false);
    }
  },

  // ─── Parámetros de acreditación por par cuenta<->medio (WU6c, CFG-001) ────

  /**
   * Compone la tabla completa (cuenta activa) x (medio vinculado a esa cuenta) llamando
   * `listarMediosDeCuenta` por cada cuenta activa — no hay un endpoint único que devuelva
   * todos los pares de una vez. Usa el service directo (no `dispatch('fetchMediosDeCuenta')`)
   * para no pisar el estado `medios`/`mediosLoading` de un solo cuenta seleccionada, que
   * alimenta `VinculoMediosPagoView.vue`.
   */
  async fetchParametrosAcreditacion({ commit, dispatch, state, getters }) {
    try {
      commit('SET_PARAMETROS_ACREDITACION_LOADING', true);
      commit('SET_PARAMETROS_ACREDITACION_ERROR', null);
      if (!state.cuentas.length) {
        await dispatch('fetchCuentas');
      }
      const cuentas = getters.cuentasActivas;
      const respuestas = await Promise.all(
        cuentas.map((cuenta) => cuentasEmpresaService.listarMediosDeCuenta(cuenta.id_cuenta))
      );
      const filas = cuentas.flatMap((cuenta, indice) => {
        const vinculos = desempaquetar(respuestas[indice]);
        return vinculos.map((vinculo) => ({
          id_cuenta: cuenta.id_cuenta,
          cuenta: cuenta.cuenta,
          id_medio_pago: vinculo.id_medio_pago,
          nombre_medio: vinculo?.medio_pago?.medio_pago || `Medio ${vinculo.id_medio_pago}`,
          plazo_acreditacion_dias: vinculo.plazo_acreditacion_dias ?? null,
          comision_pct: vinculo.comision_pct ?? null,
        }));
      });
      commit('SET_PARAMETROS_ACREDITACION', filas);
      return filas;
    } catch (error) {
      commit('SET_PARAMETROS_ACREDITACION_ERROR', error.message || 'Error al cargar los parámetros de acreditación');
      throw error;
    } finally {
      commit('SET_PARAMETROS_ACREDITACION_LOADING', false);
    }
  },

  /**
   * `plazo_acreditacion_dias`/`comision_pct` en `null` limpia el valor (CFG-003) — se envían
   * tal cual, sin filtrar por `hasOwnProperty`: el editor de la pantalla siempre edita el par
   * completo, nunca un campo aislado.
   */
  async actualizarParametroAcreditacion({ commit }, { id_cuenta, id_medio_pago, plazo_acreditacion_dias, comision_pct }) {
    try {
      commit('SET_ACTUALIZANDO_PARAMETRO', true);
      commit('SET_ACTUALIZAR_PARAMETRO_ERROR', null);
      await cuentasEmpresaService.actualizarParametrosAcreditacionMedio(id_cuenta, id_medio_pago, {
        plazo_acreditacion_dias,
        comision_pct,
      });
      const parametro = { id_cuenta, id_medio_pago, plazo_acreditacion_dias, comision_pct };
      commit('UPDATE_PARAMETRO_ACREDITACION', parametro);
      return parametro;
    } catch (error) {
      commit(
        'SET_ACTUALIZAR_PARAMETRO_ERROR',
        error.response?.data?.message || error.message || 'Error al actualizar los parámetros de acreditación'
      );
      throw error;
    } finally {
      commit('SET_ACTUALIZANDO_PARAMETRO', false);
    }
  },
};

const mutations = {
  SET_CUENTAS(s, v) { s.cuentas = v; },
  SET_CUENTAS_LOADING(s, v) { s.cuentasLoading = v; },
  SET_CUENTAS_ERROR(s, v) { s.cuentasError = v; },
  SET_MEDIOS(s, v) { s.medios = v; },
  SET_MEDIOS_LOADING(s, v) { s.mediosLoading = v; },
  SET_MEDIOS_ERROR(s, v) { s.mediosError = v; },
  SET_CATALOGO_MEDIOS(s, v) { s.catalogoMedios = v; },
  SET_DIAGNOSTICO(s, v) { s.diagnostico = v; },
  SET_DIAGNOSTICO_LOADING(s, v) { s.diagnosticoLoading = v; },
  SET_DIAGNOSTICO_ERROR(s, v) { s.diagnosticoError = v; },
  SET_CONFIRMAR_CORTE_LOADING(s, v) { s.confirmarCorteLoading = v; },
  SET_CONFIRMAR_CORTE_ERROR(s, v) { s.confirmarCorteError = v; },
  SET_ESTADO_CUENTA(s, v) { s.estadoCuenta = v; },
  SET_ESTADO_CUENTA_LOADING(s, v) { s.estadoCuentaLoading = v; },
  SET_ESTADO_CUENTA_ERROR(s, v) { s.estadoCuentaError = v; },
  SET_ESTADO_CUENTA_DESCARGANDO(s, v) { s.estadoCuentaDescargando = v; },
  SET_CUENTAS_DESTINO_REIMPUTACION(s, v) { s.cuentasDestinoReimputacion = v; },
  SET_CUENTAS_DESTINO_REIMPUTACION_LOADING(s, v) { s.cuentasDestinoReimputacionLoading = v; },
  SET_CUENTAS_DESTINO_REIMPUTACION_ERROR(s, v) { s.cuentasDestinoReimputacionError = v; },
  SET_DESTINOS_REIMPUTACION(s, v) { s.destinosReimputacion = v; },
  SET_DESTINOS_REIMPUTACION_LOADING(s, v) { s.destinosReimputacionLoading = v; },
  SET_DESTINOS_REIMPUTACION_ERROR(s, v) { s.destinosReimputacionError = v; },
  SET_PARAMETROS_ACREDITACION(s, v) { s.parametrosAcreditacion = v; },
  SET_PARAMETROS_ACREDITACION_LOADING(s, v) { s.parametrosAcreditacionLoading = v; },
  SET_PARAMETROS_ACREDITACION_ERROR(s, v) { s.parametrosAcreditacionError = v; },
  SET_ACTUALIZANDO_PARAMETRO(s, v) { s.actualizandoParametro = v; },
  SET_ACTUALIZAR_PARAMETRO_ERROR(s, v) { s.actualizarParametroError = v; },
  UPDATE_PARAMETRO_ACREDITACION(s, { id_cuenta, id_medio_pago, plazo_acreditacion_dias, comision_pct }) {
    const fila = s.parametrosAcreditacion.find(
      (f) => f.id_cuenta === id_cuenta && f.id_medio_pago === id_medio_pago
    );
    if (fila) {
      fila.plazo_acreditacion_dias = plazo_acreditacion_dias;
      fila.comision_pct = comision_pct;
    }
  },
};

export default { namespaced: true, state, getters, actions, mutations };
